import { forwardRef, type ComponentType, type SVGProps, type HTMLAttributes } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Heading, Text } from '@bn-apps/design-system-ui-react';
import { BrandArrowRight } from '@bn-apps/design-system-icons';
import styles from './NavigationCard.module.scss';

export interface NavigationCardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'color'> {
    /** The route to navigate to */
    readonly to: string;
    /** The card heading text */
    readonly heading: string;
    /** Optional description text */
    readonly description?: string;
    /** Optional custom icon (defaults to arrow) */
    readonly icon?: ComponentType<SVGProps<SVGSVGElement>>;
    /** Color variant based on design system skin colors */
    readonly color?: 'blue' | 'gray' | 'green' | 'olive' | 'orange' | 'red' | 'violet' | 'yellow';
    /** Size of the heading */
    readonly headingSize?: 'sm' | 'md' | 'lg';
    /** Additional CSS classes */
    readonly className?: string;
    /** Language attribute for the heading (useful for hyphenation) */
    readonly lang?: string;
}

/**
 * NavigationCard component for creating clickable cards that link to different routes.
 * Follows the Bane NOR design system patterns.
 */
const NavigationCard = forwardRef<HTMLDivElement, NavigationCardProps>(function NavigationCard(
    {
        to,
        heading,
        description,
        icon,
        color,
        headingSize = 'md',
        className,
        lang,
        ...rest
    },
    ref
) {
    const colorClass = color ? styles[`bn-navigation-card--${color}`] : undefined;
    const componentClassNames = [
        styles['bn-navigation-card'],
        colorClass,
        className
    ].filter(Boolean).join(' ');

    const IconComponent = icon || BrandArrowRight;

    return (
        <div ref={ref} className={componentClassNames} {...rest}>
            <RouterLink to={to} className={styles['bn-navigation-card__link']}>
                <div className={styles['bn-navigation-card__content']}>
                    <div className={styles['bn-navigation-card__text']}>
                        <Heading
                            level={3}
                            fontSize={headingSize}
                            className={styles['bn-navigation-card__heading']}
                            lang={lang}
                        >
                            {heading}
                        </Heading>

                        {description && (
                            <Text fontSize="sm" className={styles['bn-navigation-card__description']}>
                                {description}
                            </Text>
                        )}

                        <div className={styles['bn-navigation-card__action']}>
                            <IconComponent />
                        </div>
                    </div>
                </div>
            </RouterLink>
        </div>
    );
});

NavigationCard.displayName = 'NavigationCard';

export { NavigationCard };
export default NavigationCard;
